/**
 * Menu ordering for the admin "Menú" page (spec 010-menu-platos-tpv).
 *
 * Dishes are shown in three blocks: new (active without photo, pending
 * onboarding), active (with photo) and inactive. Inside each block they are
 * sorted by course (Entrantes, Principales, Postres, other categories
 * alphabetically, no category last) and then by name.
 */
import type { Item } from '@/types/item';

export type MenuStatus = 'new' | 'active' | 'inactive';

export const MENU_STATUS_ORDER: MenuStatus[] = ['new', 'active', 'inactive'];

export const MENU_STATUS_LABELS: Record<MenuStatus, string> = {
  new: 'Nuevos',
  active: 'Activos',
  inactive: 'Inactivos',
};

/** Course order. Matches Item::CATEGORY_ORDER in the API for the first three. */
export const COURSE_ORDER = ['Entrantes', 'Principales', 'Postres'];

type StatusInput = Pick<Item, 'active' | 'photos'>;

export function itemStatus(item: StatusInput): MenuStatus {
  if (!item.active) return 'inactive';
  return item.photos.length === 0 ? 'new' : 'active';
}

function courseRank(category: string | null | undefined): number {
  if (!category) return COURSE_ORDER.length + 1;
  const index = COURSE_ORDER.indexOf(category);
  return index === -1 ? COURSE_ORDER.length : index;
}

export function compareCategory(a: string | null | undefined, b: string | null | undefined): number {
  const rank = courseRank(a) - courseRank(b);
  if (rank !== 0) return rank;
  return (a ?? '').localeCompare(b ?? '', 'es');
}

export function compareMenuItems(a: Item, b: Item): number {
  return compareCategory(a.category, b.category) || a.name.localeCompare(b.name, 'es');
}

export interface MenuGroup {
  status: MenuStatus;
  items: Item[];
}

/** Splits items into the three status blocks, each sorted by course and name. Empty blocks are kept. */
export function groupMenuItems(items: Item[]): MenuGroup[] {
  return MENU_STATUS_ORDER.map((status) => ({
    status,
    items: items.filter((item) => itemStatus(item) === status).sort(compareMenuItems),
  }));
}

export function countByStatus(items: StatusInput[]): Record<MenuStatus, number> {
  const counts: Record<MenuStatus, number> = { new: 0, active: 0, inactive: 0 };
  items.forEach((item) => {
    counts[itemStatus(item)] += 1;
  });
  return counts;
}
